import {
  apiErrorCodeOf,
  GROOMING_BELOW_BAR_ERROR_CODE,
  GROOMING_QUEUE_SETUP_COPY,
  isGroomingEngineErrorCode,
  isGroomingQueueSetupCode,
  TOOLBOX_DOWNLOAD_PAGE_PATH,
  type GroomingApiErrorCode,
  type GroomingEngineErrorCode,
  type GroomingRepoHost,
  type GroomingStopReason,
} from "@ttc-spectree/toolbox-contract";
import { ApiError } from "@/lib/api/client";
import { projectPath } from "@/lib/grooming/paths";
import { isFixItSetupStep, setupStepPath, TOOLBOX_SETUP_PATH } from "@/lib/toolbox/page-names";

/**
 * Plain words for what went wrong in a grooming run (epic PI-SPEC-SW-E279,
 * task PI-SPEC-SW-1392-2; Claude Design "All Screens" frame 2e; rewritten
 * for UX-PM-07 in epic PI-SPEC-SW-E290, feature PI-SPEC-SW-1478, frame B3).
 *
 * The engine reports a code from `@ttc-spectree/toolbox-contract`
 * (`GROOMING_ENGINE_ERROR_CODES`) and a message written for engineers. The
 * page shows what happened and one thing to do, in plain words; a setup
 * problem's one thing is "Fix it" ({@link groomingFixPath}). The engine
 * message, the code and the exact time stay behind "Copy details for the
 * Admin" (`copy-details.ts`). No command, plugin, server, token type or
 * "first-launch checks" in any of these texts; a guard test
 * (`__tests__/plain-run-words.test.ts`) keeps it that way.
 */

export interface GroomingErrorMessage {
  /** What happened, in one plain sentence. */
  cause: string;
  /** The one thing the person can do about it. */
  whatToDo: string;
}

/**
 * A setup problem's "What to do": the one action is Fix it, then try again.
 * These texts show on a stopped session run (Resume), a failed item (Retry)
 * and One item, none of which starts again by itself, so they never promise
 * that. The Queue's own pause banner says the queue starts again by itself
 * (`queuePauseBanner` and `fixItHint`), because there it does.
 */
const FIX_IT_TAIL = "Then try again.";

/** Every engine code, plus the API's below-the-bar code. */
export const GROOMING_ERROR_MESSAGES: Readonly<Record<GroomingEngineErrorCode, GroomingErrorMessage>> = {
  CLAUDE_NOT_INSTALLED: {
    cause: "Claude Code is missing from this computer.",
    whatToDo: `Click Fix it to install it. ${FIX_IT_TAIL}`,
  },
  CLAUDE_NOT_SIGNED_IN: {
    cause: "Claude needs you to sign in again.",
    whatToDo: `Click Fix it to sign in. ${FIX_IT_TAIL}`,
  },
  PLUGIN_MISSING: {
    cause: "Part of the Toolbox that Claude needs is missing or out of date.",
    whatToDo: "Click Fix it to get the latest version. Try again after the reinstall.",
  },
  MCP_NOT_CONNECTED: {
    cause: "Claude could not reach SpecTree through the Toolbox on this computer.",
    whatToDo: "Click Fix it to get the latest version. Try again after the reinstall.",
  },
  USAGE_LIMIT: {
    cause: "Your Claude usage limit was reached.",
    whatToDo: "Nothing is lost. Resume once your limit resets.",
  },
  NETWORK: {
    cause: "This computer can't reach SpecTree or Azure DevOps.",
    whatToDo: `Click Fix it to check the connection. ${FIX_IT_TAIL}`,
  },
  ITEM_NOT_READY: {
    cause: "This item does not have what the team needs to size it yet.",
    whatToDo: "Ask its author for the missing information, or fix it in the ADO Formulator.",
  },
  SAVE_FAILED: {
    cause: "Claude finished the brief, but SpecTree could not save it.",
    whatToDo: "Retry this item. If it fails again, copy the details for the Admin.",
  },
  UNKNOWN: {
    cause: "Claude could not finish this brief.",
    whatToDo: "Retry this item. If it fails again, copy the details for the Admin.",
  },
  REPO_AUTH_REQUIRED: {
    cause: "Azure DevOps wants you to sign in again to read the project's code.",
    whatToDo: `Click Fix it to sign in again. ${FIX_IT_TAIL}`,
  },
  GIT_NOT_INSTALLED: {
    cause: "Git, which reads the project's code, is missing from this computer.",
    whatToDo: `Click Fix it to install it. ${FIX_IT_TAIL}`,
  },
  ADO_PAT_INVALID: {
    cause: "Your Azure DevOps token has run out or no longer works.",
    whatToDo: `Click Fix it to connect Azure DevOps again. It takes about 2 minutes. ${FIX_IT_TAIL}`,
  },
};

const BELOW_BAR_MESSAGE: GroomingErrorMessage = GROOMING_ERROR_MESSAGES.ITEM_NOT_READY;

/**
 * `REPO_AUTH_REQUIRED` when the repository that asked is on GitHub (epic
 * PI-SPEC-SW-E318, feature PI-SPEC-SW-1679): GitHub's sign-in is "Sign in to
 * GitHub" on Setup's Project code step (ER-1683 Resolved Decision 1). The
 * Azure DevOps entry above stays exactly as it was.
 */
export const GITHUB_REPO_AUTH_MESSAGE: GroomingErrorMessage = {
  cause: "GitHub wants you to sign in again to read the project's code.",
  whatToDo: `Click Fix it, then Sign in to GitHub on Setup. ${FIX_IT_TAIL}`,
};

/**
 * Plain words for an item or run error code; unknown codes read as UNKNOWN.
 * `host` is where the failing repository lives, when the caller knows it
 * (`repoHostByName` in `repo-host.ts`): `"github"` gives the GitHub sign-in
 * words for `REPO_AUTH_REQUIRED`; absent or `"ado"` changes nothing.
 */
export function groomingErrorMessage(code: string | null | undefined, host?: GroomingRepoHost | null): GroomingErrorMessage {
  if (code === "REPO_AUTH_REQUIRED" && host === "github") return GITHUB_REPO_AUTH_MESSAGE;
  if (code === GROOMING_BELOW_BAR_ERROR_CODE) return BELOW_BAR_MESSAGE;
  if (isGroomingEngineErrorCode(code)) return GROOMING_ERROR_MESSAGES[code];
  return GROOMING_ERROR_MESSAGES.UNKNOWN;
}

/** The "environment" stop: something on this computer needs fixing, with no code to say what. */
const ENVIRONMENT_MESSAGE: GroomingErrorMessage = {
  cause: "Something on this computer needs fixing before Claude can go on.",
  whatToDo: `Click Fix it to see what it needs. ${FIX_IT_TAIL}`,
};

/** Plain words for why a run stopped, when the engine sent no code. */
export function stopReasonMessage(reason: GroomingStopReason): GroomingErrorMessage {
  switch (reason) {
    case "usage-limit":
      return GROOMING_ERROR_MESSAGES.USAGE_LIMIT;
    case "user":
      return { cause: "You stopped the run.", whatToDo: "Nothing is lost. Resume when you are ready." };
    case "environment":
      return ENVIRONMENT_MESSAGE;
    case "error":
      return {
        cause: "The run stopped because of an error.",
        whatToDo: "Resume to try the remaining items again. If it stops again, copy the details for the Admin.",
      };
  }
}

/** Where Setup lives inside the Toolbox (kept here for existing callers; see `page-names.ts`). */
export { TOOLBOX_SETUP_PATH };

/**
 * Where "Fix it" goes for a setup problem, or null when the code is not one
 * (an item problem: Retry, or the Formulator). Follows the contract's
 * wording sheet (`GROOMING_QUEUE_SETUP_COPY[code].fixAction`): a newer app
 * goes to the download page, a repository sign-in to the project's Project
 * page (Repositories tab) when the project is known, a Setup step's code to that step of
 * Setup, everything else to Setup. `environment` (a stop with no code) also
 * goes to Setup. A repository sign-in for a GitHub repository (`host`
 * `"github"`, epic PI-SPEC-SW-E318) goes to Setup's Project code step, where
 * "Sign in to GitHub" is.
 */
export function groomingFixPath(
  code: string | null | undefined,
  project?: string | null,
  host?: GroomingRepoHost | null
): string | null {
  if (code === "environment") return TOOLBOX_SETUP_PATH;
  if (!isGroomingQueueSetupCode(code)) return null;
  if (code === "REPO_AUTH_REQUIRED" && host === "github") return setupStepPath("project-code");
  switch (GROOMING_QUEUE_SETUP_COPY[code].fixAction) {
    case "get-update":
      return TOOLBOX_DOWNLOAD_PAGE_PATH;
    case "repo-sign-in":
      return project ? projectPath(project, "repositories") : TOOLBOX_SETUP_PATH;
    case "open-setup-step": {
      const step = GROOMING_QUEUE_SETUP_COPY[code].setupStep;
      return setupStepPath(isFixItSetupStep(step) ? step : null);
    }
    default:
      return TOOLBOX_SETUP_PATH;
  }
}

/** "Copy details for the Admin", kept here for existing callers; see `copy-details.ts`. */
export { adminDetailsText, errorDetailsText, type AdminDetails } from "@/lib/grooming/copy-details";

/**
 * The most specific code of a failed SpecTree request: `error.details.code`
 * when the API put one there (a generic class code such as FORBIDDEN or
 * CONFLICT carries the grooming code in details), else `error.code`.
 */
export function groomingApiErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  return apiErrorCodeOf(error.data) ?? undefined;
}

// ---------------------------------------------------------------------------
// Agenda edits between meetings (grooming contract 4, epic PI-SPEC-SW-E323,
// ER-1710): agendas stay editable until the meeting date passes.
// ---------------------------------------------------------------------------

/** The contract 4 API codes an add, move, remove or date change can answer with. */
export type GroomingAgendaErrorCode = Extract<
  GroomingApiErrorCode,
  | "GROOMING_AGENDA_LOCKED"
  | "GROOMING_SESSION_DATE_NOT_UPCOMING"
  | "GROOMING_SESSION_DATE_TAKEN"
  | "GROOMING_BATCH_ITEM_REMOVED"
>;

/** One plain sentence per contract 4 agenda code. */
export const GROOMING_AGENDA_ERROR_MESSAGES: Readonly<Record<GroomingAgendaErrorCode, string>> = {
  GROOMING_AGENDA_LOCKED: "This meeting has passed, so its agenda is now a record and cannot change.",
  GROOMING_SESSION_DATE_NOT_UPCOMING: "Pick one of the upcoming meetings.",
  GROOMING_SESSION_DATE_TAKEN: "Another agenda is already set for that meeting. Move items to it instead.",
  GROOMING_BATCH_ITEM_REMOVED: "This item was taken off the agenda.",
};

function isAgendaErrorCode(code: string | null | undefined): code is GroomingAgendaErrorCode {
  return typeof code === "string" && Object.prototype.hasOwnProperty.call(GROOMING_AGENDA_ERROR_MESSAGES, code);
}

/**
 * The plain sentence for a failed agenda edit (add, move, remove, change
 * date) when SpecTree answered with a contract 4 agenda code, else null so the
 * screen keeps its own wording for every other failure. Accepts the failed
 * request's error or the code itself.
 */
export function agendaErrorMessage(errorOrCode: unknown): string | null {
  const code = typeof errorOrCode === "string" ? errorOrCode : groomingApiErrorCode(errorOrCode);
  return isAgendaErrorCode(code) ? GROOMING_AGENDA_ERROR_MESSAGES[code] : null;
}
